/**
 * @fileoverview Story list / story viewer page translations for all supported locales.
 */

import type { SupportedLang } from './lang.js';
import { currentLang } from './lang.js';

interface StoryStrings {
    pageTitle:       string;
    age:             string;  // "{n}+" is appended by the component
    pages:           string;  // prepended with the count
    readBadge:       string;
    buyNow:          string;
    // viewer
    loading:         string;
    purchaseFirst:   string;
    storyCompleted:  string;
    back:            string;
    previous:        string;
    next:            string;
    finish:          string;
    pageOf:          (current: number, total: number) => string;
}

const storyTranslations: Record<SupportedLang, StoryStrings> = {
    en: {
        pageTitle:      '📖 Bedtime Stories',
        age:            'Age',
        pages:          'pages',
        readBadge:      '✓ Read',
        buyNow:         'Buy Now',
        loading:        'Loading…',
        purchaseFirst:  'Please purchase this story first!',
        storyCompleted: 'Story completed! 🌟',
        back:           '← Back',
        previous:       '◀ Previous',
        next:           'Next ▶',
        finish:         'Finish',
        pageOf:         (c, t) => `Page ${c} of ${t}`,
    },
    de: {
        pageTitle:      '📖 Gutenachtgeschichten',
        age:            'Alter',
        pages:          'Seiten',
        readBadge:      '✓ Lesen',
        buyNow:         'Kaufen',
        loading:        'Lädt…',
        purchaseFirst:  'Bitte kaufe diese Geschichte zuerst!',
        storyCompleted: 'Geschichte beendet! 🌟',
        back:           '← Zurück',
        previous:       '◀ Zurück',
        next:           'Weiter ▶',
        finish:         'Fertig',
        pageOf:         (c, t) => `Seite ${c} von ${t}`,
    },
    es: {
        pageTitle:      '📖 Cuentos para dormir',
        age:            'Edad',
        pages:          'páginas',
        readBadge:      '✓ Leer',
        buyNow:         'Comprar',
        loading:        'Cargando…',
        purchaseFirst:  '¡Por favor, compra este cuento primero!',
        storyCompleted: '¡Cuento terminado! 🌟',
        back:           '← Volver',
        previous:       '◀ Anterior',
        next:           'Siguiente ▶',
        finish:         'Terminar',
        pageOf:         (c, t) => `Página ${c} de ${t}`,
    },
    fr: {
        pageTitle:      '📖 Histoires du soir',
        age:            'Âge',
        pages:          'pages',
        readBadge:      '✓ Lire',
        buyNow:         'Acheter',
        loading:        'Chargement…',
        purchaseFirst:  "Veuillez d'abord acheter cette histoire !",
        storyCompleted: 'Histoire terminée ! 🌟',
        back:           '← Retour',
        previous:       '◀ Précédent',
        next:           'Suivant ▶',
        finish:         'Terminer',
        pageOf:         (c, t) => `Page ${c} sur ${t}`,
    },
    pt: {
        pageTitle:      '📖 Histórias para dormir',
        age:            'Idade',
        pages:          'páginas',
        readBadge:      '✓ Ler',
        buyNow:         'Comprar',
        loading:        'A carregar…',
        purchaseFirst:  'Por favor, compra esta história primeiro!',
        storyCompleted: 'História concluída! 🌟',
        back:           '← Voltar',
        previous:       '◀ Anterior',
        next:           'Seguinte ▶',
        finish:         'Terminar',
        pageOf:         (c, t) => `Página ${c} de ${t}`,
    },
    bs: {
        pageTitle:      '📖 Priče za laku noć',
        age:            'Uzrast',
        pages:          'stranica',
        readBadge:      '✓ Pročitano',
        buyNow:         'Kupi sada',
        loading:        'Učitavanje…',
        purchaseFirst:  'Molimo vas da prvo kupite ovu priču!',
        storyCompleted: 'Priča je završena! 🌟',
        back:           '← Nazad',
        previous:       '◀ Prethodna',
        next:           'Sljedeća ▶',
        finish:         'Završi',
        pageOf:         (c, t) => `Stranica ${c} od ${t}`,
    },
};

export const storyStrings: StoryStrings = storyTranslations[currentLang];
